import { IOrderProduct } from 'sections/Order/types'
import { IProduct } from 'featureSliced/shared/store/reducers/CategoriesReducer/types'
import {
  IDiscount,
  IIndivPrice,
  IPriceList,
} from 'featureSliced/shared/store/reducers/SpecialsReducer/types'
import { convertToPrice } from 'featureSliced/shared/utils/convertToPrice'
import { ICartProduct } from 'featureSliced/shared/store/generalTypes'
import {
  IBucketProduct,
  ProductTemplateIdType,
} from 'featureSliced/shared/store/reducers/QRMenuBucketReducer/types'

import { getPriceWithoutDiscount } from './getPriceWithoutDiscount'
import { getPrice } from './getPrice'
import { useMySelector } from '../useMySelector'

export const getAllProductsPrice = (
  productList: IBucketProduct[],
  discounts: IDiscount[],
  individualPrices: IIndivPrice[],
  productListWithPrices: IPriceList,
  currency: string
) => {
  const totalProductsPrice = productList?.reduce((acc, product) => {
    const productPrices = getProductPrice(
      product,
      product?.productTemplateId,
      product?.menuItems?.length || 0,
      discounts,
      individualPrices,
      productListWithPrices,
      currency
    )
    const totalMenuItemsPrice =
      product?.menuItems?.reduce((acc, menuItem, index) => {
        const stepIndex =
          typeof menuItem === 'object' &&
          menuItem !== null &&
          'menuIndex' in menuItem
            ? (menuItem as { menuIndex?: number }).menuIndex ?? index
            : index
        const itemPrice = getMenuItemPrice(menuItem as any, currency)
        return (acc += itemPrice?.finalPrice)
      }, 0) || 0
    return (acc +=
      (productPrices?.finalPrice + totalMenuItemsPrice) * product?.count)
  }, 0)
  return totalProductsPrice
}

export const getProductPrice = (
  product: IProduct | IOrderProduct | ICartProduct,
  productTemplateId: ProductTemplateIdType,
  menuItemsLength: number,
  discounts: IDiscount[],
  individualPrices: IIndivPrice[],
  productListWithPrices: IPriceList,
  currency: string
) => {
  const productId = (product as any)?.id ?? (product as any)?.productId

  const productPrices =
    productListWithPrices?.[`${productId}-${productTemplateId}`]
  const discount = discounts?.find((dis) => dis.productId === productId)
  const individualPrice = individualPrices?.find(
    (item) => item.productId === productId
  )

  let priceOG, finalPrice, discountValue

  switch (true) {
    case Boolean(menuItemsLength !== 0):
      priceOG = getPrice(product)
      finalPrice = getPrice(product)
      discountValue = 0
      break
    case Boolean(productTemplateId === 'discount') || Boolean(discount):
      discountValue = discount?.discountPercentage || 0
      priceOG = getPriceWithoutDiscount(productPrices)
      finalPrice = getPrice(productPrices)
      break
    case Boolean(productTemplateId === 'individualPrice') ||
      Boolean(individualPrice):
      priceOG = getPriceWithoutDiscount(individualPrice)
      finalPrice = getPrice(individualPrice)
      discountValue = 0
      break
    case typeof productTemplateId === 'number' && !individualPrice: {
      const priceFromList = getPrice(productPrices)
      priceOG = priceFromList
        ? getPriceWithoutDiscount(productPrices)
        : getPrice(product)
      finalPrice = priceFromList || getPrice(product)
      discountValue = 0
      break
    }
    default:
      priceOG = getPrice(product)
      finalPrice = getPrice(product)
      discountValue = 0
  }

  return {
    priceOG,
    priceOGString: convertToPrice(priceOG),
    finalPrice: finalPrice || priceOG,
    finalPriceString: convertToPrice(finalPrice || priceOG),
    discountValue,
    discountString: discount?.discountPercentage
      ? `-${discount?.discountPercentage}%`
      : '',
    currency,
  }
}

const getMenuItemPrice = (menuItem: any, currency: string) => {
  const finalPrice = getPrice(menuItem)
  return {
    priceOG: finalPrice,
    priceOGString: convertToPrice(finalPrice),
    finalPrice,
    finalPriceString: convertToPrice(finalPrice),
    currency,
  }
}

export const useGetProductPrice = () => {
  const currency = useMySelector((state) => state.app.companyData.Currency)
  const { discounts, individualPrices, productListWithPrices } = useMySelector(
    (state) => state.specials
  )

  return {
    getAllProductsPrice: (productsList: IBucketProduct[]) =>
      getAllProductsPrice(
        productsList,
        discounts,
        individualPrices,
        productListWithPrices,
        currency
      ),
    getProductPrice: (
      product: IProduct | IOrderProduct | ICartProduct,
      productTemplateId: ProductTemplateIdType,
      menuItemsLength = 0
    ) =>
      getProductPrice(
        product,
        productTemplateId,
        menuItemsLength,
        discounts,
        individualPrices,
        productListWithPrices,
        currency
      ),
    getMenuItemPrice: (menuItem: any) => getMenuItemPrice(menuItem, currency),
  }
}
